import React, { useDeferredValue, useMemo, useState } from 'react';
import apiClient from '../../../services/api';
import {
  MOVE_STEADY,
  baseRowKey,
  seedHorizon,
  sizeStraddleRows,
} from '../../../utils/straddleCalculations';
import {
  DEFAULT_SIZING,
  EMPTY_FILTERS,
  budgetsWithinLimit,
  resolveBudgets,
} from '../../../utils/straddleMappings';
import StraddleForm from './StraddleForm';
import StraddleFilterControls from './StraddleFilterControls';
import StraddleHeatmapView from './StraddleHeatmapView';
import StraddleDetailTable from './StraddleDetailTable';
import StraddleSizingControls from './StraddleSizingControls';

const VIEWS = [
  { key: 'heatmap', label: 'Elasticity Heatmap' },
  { key: 'detail', label: 'Detail View' },
];

/** Flatten a DRF error body, which nests a list per submitted pair, into readable text. */
const formatFieldErrors = (node, path = '') => {
  if (typeof node === 'string') return [path ? `${path}: ${node}` : node];
  if (Array.isArray(node)) return node.flatMap((item) => formatFieldErrors(item, path));
  if (node && typeof node === 'object') {
    return Object.entries(node).flatMap(([key, value]) => (
      // A cross-field error isn't about a field named "non_field_errors".
      formatFieldErrors(value, key === 'non_field_errors' ? path : key)
    ));
  }
  return [];
};

const describeSkipped = (skipped, collapsed) => {
  const parts = [];
  if (skipped?.no_date) parts.push(`${skipped.no_date} with no listed date`);
  if (skipped?.no_strike) parts.push(`${skipped.no_strike} with no strike in range`);
  if (skipped?.no_quote) parts.push(`${skipped.no_quote} with no usable quote`);
  const notes = [];
  if (parts.length) notes.push(`Pairs not shown: ${parts.join(', ')}.`);
  if (collapsed) {
    notes.push(`${collapsed} requested pair${collapsed === 1 ? '' : 's'} landed on contracts already listed.`);
  }
  return notes.join(' ');
};

/**
 * Straddle Simulator.
 *
 * Scans buy-call + buy-put pairs where each leg has its own strike distance and
 * expiration, then compares them in an elasticity heatmap. Self-contained like
 * SpreadsTab: it owns its data, loading and error state and its own ticker input.
 */
function StraddlesTab() {
  const [scanData, setScanData] = useState(null);
  const [runId, setRunId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('heatmap');

  // Shared across both views so state survives a view switch, as in SpreadsTab.
  const [favoritedRows, setFavoritedRows] = useState(new Set());
  const [removedRows, setRemovedRows] = useState(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sharedDaysToExit, setSharedDaysToExit] = useState(null);
  const [defaultDays, setDefaultDays] = useState(30);
  const [daysToExitMode, setDaysToExitMode] = useState('fixed');
  const [daysToExitPercent, setDaysToExitPercent] = useState(null);
  const [moveTiming, setMoveTiming] = useState(MOVE_STEADY);
  const [ivShiftPct, setIvShiftPct] = useState(0);
  const [sizing, setSizing] = useState(DEFAULT_SIZING);

  const results = useMemo(() => scanData?.results || [], [scanData]);
  const scanErrors = scanData?.errors || [];

  // Both views get rows already sized, so everything they read describes the real
  // position. Deferred because a keystroke can reprice hundreds of rows.
  const deferredSizing = useDeferredValue(sizing);
  const budgets = useMemo(() => resolveBudgets(deferredSizing), [deferredSizing]);
  const shownBudgets = useMemo(
    () => budgetsWithinLimit(budgets, results.length),
    [budgets, results.length]
  );
  const hiddenBudgets = budgets.slice(shownBudgets.length);
  const sizedResults = useMemo(
    () => sizeStraddleRows(results, shownBudgets),
    [results, shownBudgets]
  );

  // Only rows with a budget carry these fields, and a split can be deselected while
  // a filter is set on it. Either would otherwise hide rows with no way to tell why.
  const activeFilters = useMemo(() => {
    if (shownBudgets.length === 0) return { ...filters, budgets: [], splits: [] };
    const labels = new Set(shownBudgets.map((budget) => budget.label));
    return { ...filters, splits: filters.splits.filter((label) => labels.has(label)) };
  }, [filters, shownBudgets]);

  // Stars stay remembered for a split that's deselected and come back with it, but
  // only rows on screen count, so the Favorites total matches what can be seen.
  const shownFavorites = useMemo(() => {
    const shown = new Set(sizedResults.map((row) => row._key));
    return new Set([...favoritedRows].filter((key) => shown.has(key)));
  }, [sizedResults, favoritedRows]);
  const skippedNote = scanData ? describeSkipped(scanData.skipped, scanData.collapsed) : '';

  const handleRun = async (payload) => {
    setLoading(true);
    setError(null);
    setLoadingMessage(`Pricing straddles for ${payload.tickers.join(', ')}...`);

    try {
      const response = await apiClient.post('/stock-analyzer/straddles/', payload);
      const rows = response.data?.results || [];
      const seed = seedHorizon(rows);

      // Stars and drops carry over for contracts that come back, as on the screener.
      // Keys that didn't return are removed so the Favorites count stays accurate.
      const returned = new Set(rows.map((row) => row._key));
      const keptFrom = (prev) => new Set([...prev].filter((key) => returned.has(baseRowKey(key))));

      // All set together, so no render can pair new rows with the old horizon.
      setScanData(response.data);
      setRunId((id) => id + 1);
      setFavoritedRows(keptFrom);
      setRemovedRows(keptFrom);
      setShowFavoritesOnly(false);
      setFilters(EMPTY_FILTERS);
      setDefaultDays(seed);
      setSharedDaysToExit(seed);
      setDaysToExitMode('fixed');
      setDaysToExitPercent(null);
    } catch (err) {
      console.error('Straddle simulation failed:', err);
      const details = err.response?.data?.details;
      const fieldErrors = details ? formatFieldErrors(details) : [];
      setError(
        fieldErrors.length
          ? fieldErrors.join(' • ')
          : (err.response?.data?.error || 'Failed to run straddle simulation')
      );
      setScanData(null);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const sharedViewProps = {
    results: sizedResults,
    favoritedRows: shownFavorites,
    setFavoritedRows,
    removedRows,
    setRemovedRows,
    showFavoritesOnly,
    setShowFavoritesOnly,
    sharedDaysToExit,
    setSharedDaysToExit,
    daysToExitMode,
    setDaysToExitMode,
    daysToExitPercent,
    setDaysToExitPercent,
    defaultDays,
    filters: activeFilters,
    moveTiming,
    ivShiftPct,
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">Straddle Simulator</h2>
        <p className="text-gray-400 text-sm">
          Buy a call and a put together, each with its own strike and expiration. Scan many
          pairings across tickers, then see what each one returns if the stock moves either
          way, 10, 30 or 90 days out.
        </p>
      </div>

      <StraddleForm
        onRun={handleRun}
        loading={loading}
        sizing={sizing}
        onSizingChange={setSizing}
        hiddenBudgets={hiddenBudgets}
      />

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4" />
            <p className="text-gray-400">{loadingMessage}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded-md p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {scanData && !loading && (
        <>
          {/* Partial failures show ALONGSIDE results: one bad leg or ticker never
              discards an otherwise good run. */}
          {(scanErrors.length > 0 || skippedNote) && (
            <div className="bg-yellow-900/20 border border-yellow-700 rounded-md p-4">
              {scanErrors.length > 0 && (
                <>
                  <h3 className="text-yellow-400 font-medium mb-2 text-sm">
                    {scanData.incomplete
                      ? 'The run hit its time limit, so these results are partial:'
                      : 'Some of this run could not be priced:'}
                  </h3>
                  <ul className="text-yellow-300 text-xs space-y-1">
                    {scanErrors.map((e) => (
                      <li key={`${e.ticker}|${e.scope}|${e.error}`}>
                        • <strong>{e.ticker}</strong> ({e.scope}): {e.error}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {skippedNote && <p className="text-yellow-300 text-xs mt-2">{skippedNote}</p>}
            </div>
          )}

          {results.length > 0 && (
            <div className="border-t border-gray-700 pt-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold text-white">Simulation Results</h3>
                <span className="px-3 py-1 bg-blue-900/30 rounded-full text-blue-400 text-sm">
                  {scanData.total_found} straddle{scanData.total_found === 1 ? '' : 's'}
                </span>
              </div>

              <div
                style={{
                  display: 'flex', justifyContent: 'center', gap: 12,
                  marginBottom: 16, padding: 12, background: '#1a1a1a', borderRadius: 8,
                }}
              >
                {VIEWS.map((v) => {
                  const active = viewMode === v.key;
                  return (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => setViewMode(v.key)}
                      className={active ? 'active' : ''}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 6,
                        border: active ? '2px solid #218aff' : '1px solid #333',
                        background: active ? '#218aff20' : '#0a0a0a',
                        color: active ? '#ffffff' : '#b0b0b0',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 14,
                        transition: 'all 0.2s ease',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>

              <StraddleFilterControls results={sizedResults} filters={activeFilters} onChange={setFilters} />

              <div className="bg-gray-800 rounded-lg px-4 py-3 mb-4">
                <StraddleSizingControls sizing={sizing} onChange={setSizing} hiddenBudgets={hiddenBudgets} />
              </div>

              {viewMode === 'heatmap' ? (
                <StraddleHeatmapView
                  key={runId}
                  {...sharedViewProps}
                  setMoveTiming={setMoveTiming}
                  setIvShiftPct={setIvShiftPct}
                />
              ) : (
                <StraddleDetailTable key={runId} {...sharedViewProps} />
              )}
            </div>
          )}

          {results.length === 0 && scanErrors.length === 0 && (
            <div className="text-center py-12 border-t border-gray-700">
              <p className="text-gray-400">No tradeable straddles matched those selections.</p>
              <p className="text-gray-500 text-sm mt-2">
                Try strikes nearer the money, or a more actively traded expiration.
              </p>
            </div>
          )}
        </>
      )}

      {!scanData && !loading && !error && (
        <div className="text-center py-12 border-t border-gray-700">
          <p className="text-gray-400">
            Pick your tickers, strike distances and expirations, then run a simulation.
          </p>
        </div>
      )}
    </div>
  );
}

export default StraddlesTab;
